import { z } from 'zod'

/**
 * Phone numbers, Nepal (decision D1). Phone is the login identifier (D5) but never
 * a delivery channel — there is no SMS provider (D2).
 *
 * Canonical storage form is digits with country code and no punctuation:
 * "9779812345678". Users may type it however they like.
 */

export const NP_COUNTRY_CODE = '977'

/** Nepali mobile numbers are 10 digits beginning with 9. */
const NP_MOBILE = /^9\d{9}$/
/** Landlines: area code (1-3 digits after the leading 0) plus 6-7 subscriber digits. */
const NP_LANDLINE = /^0\d{7,9}$/

export type PhoneKind = 'mobile' | 'landline'

/**
 * Reduce any user input to canonical form, or null if it cannot be a Nepali number.
 * Accepts "+977 98-1234 5678", "098...", "9812345678", and so on.
 */
export function normalizePhone(input: string): string | null {
  let digits = input.replace(/\D/g, '')
  if (digits.startsWith('00')) digits = digits.slice(2)
  // "0977-98..." — the trunk prefix written before the country code. Stripped only
  // when 977 follows, so a landline's own leading zero ("01-444 5555") survives.
  if (digits.startsWith(`0${NP_COUNTRY_CODE}`)) digits = digits.slice(1)
  if (digits.startsWith(NP_COUNTRY_CODE) && digits.length > 10) {
    digits = digits.slice(NP_COUNTRY_CODE.length)
  }
  if (NP_MOBILE.test(digits)) return NP_COUNTRY_CODE + digits
  if (NP_LANDLINE.test(digits)) return NP_COUNTRY_CODE + digits.slice(1)
  // A landline typed without its leading zero, e.g. "14445555".
  if (/^\d{8,9}$/.test(digits)) return NP_COUNTRY_CODE + digits
  return null
}

export function phoneKind(canonical: string): PhoneKind | null {
  const local = canonical.startsWith(NP_COUNTRY_CODE)
    ? canonical.slice(NP_COUNTRY_CODE.length)
    : canonical
  if (NP_MOBILE.test(local)) return 'mobile'
  if (/^\d{8,9}$/.test(local)) return 'landline'
  return null
}

/** Display form: "+977 98-1234-5678" for mobile, "+977 1-444-5555" for landline. */
export function formatPhone(canonical: string): string {
  const local = canonical.startsWith(NP_COUNTRY_CODE)
    ? canonical.slice(NP_COUNTRY_CODE.length)
    : canonical
  if (NP_MOBILE.test(local)) {
    return `+${NP_COUNTRY_CODE} ${local.slice(0, 2)}-${local.slice(2, 6)}-${local.slice(6)}`
  }
  if (local.length >= 8) {
    const area = local.length === 9 ? local.slice(0, 2) : local.slice(0, 1)
    const rest = local.slice(area.length)
    return `+${NP_COUNTRY_CODE} ${area}-${rest.slice(0, 3)}-${rest.slice(3)}`
  }
  return `+${NP_COUNTRY_CODE} ${local}`
}

const PHONE_HELP = 'Enter a Nepali phone number, for example 98-1234-5678.'

/** Any Nepali phone: used for org contact details. Normalizes as it parses. */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required.')
  .transform((v, ctx) => {
    const normalized = normalizePhone(v)
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: PHONE_HELP })
      return z.NEVER
    }
    return normalized
  })

/** Mobile only: used for login identifiers, so people can actually be reached. */
export const mobilePhoneSchema = z
  .string()
  .trim()
  .min(1, 'Mobile number is required.')
  .transform((v, ctx) => {
    const normalized = normalizePhone(v)
    if (!normalized || phoneKind(normalized) !== 'mobile') {
      ctx.addIssue({ code: 'custom', message: 'Enter a 10-digit mobile number starting with 9.' })
      return z.NEVER
    }
    return normalized
  })

export const optionalPhoneSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    if (v === '') return null
    const normalized = normalizePhone(v)
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: PHONE_HELP })
      return z.NEVER
    }
    return normalized
  })
  .nullish()
